import { useEffect, useState } from 'react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { PRIVATE_ROUTE_REGISTRY } from 'constants/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import PermissionHandler from 'components/permission-handler';
import { SideBarItem, getSideBarItemPermissions, isSideBarGroup } from 'components/sidebar/types';
import { List, Collapse, ListItem, ListItemText, ListItemIcon, ListItemButton } from '@mui/material';

type CollapseMenuProps = {
  sideBarItem: SideBarItem;
};

const CollapseMenu = ({ sideBarItem }: CollapseMenuProps) => {
  // other hooks
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // dynamic variables
  const isGroup = isSideBarGroup(sideBarItem);
  const itemPermissions = getSideBarItemPermissions(sideBarItem);
  const itemPath = isGroup ? null : PRIVATE_ROUTE_REGISTRY[sideBarItem.routeId].path;
  const isSelectedSubMenuless = itemPath === pathname;
  const isSelectedWithSubMenu =
    isGroup && sideBarItem.subMenu.some(({ routeId }) => PRIVATE_ROUTE_REGISTRY[routeId].path === pathname);

  // useState hooks
  const [open, setOpen] = useState(Boolean(isSelectedWithSubMenu));

  // sync helper functions
  const handleClick = () => {
    setOpen((currentOpen) => !currentOpen);
  };

  useEffect(() => {
    if (isSelectedWithSubMenu) setOpen(true);
  }, [isSelectedWithSubMenu]);

  return !isGroup ? (
    <PermissionHandler requiredPermissionCodes={itemPermissions}>
      <ListItem disablePadding sx={{ display: 'block' }} onClick={() => itemPath && navigate(itemPath)}>
        <ListItemButton selected={isSelectedSubMenuless}>
          <ListItemIcon>{sideBarItem.icon}</ListItemIcon>

          <ListItemText primary={sideBarItem.name} />
        </ListItemButton>
      </ListItem>
    </PermissionHandler>
  ) : (
    <PermissionHandler requiredPermissionCodes={itemPermissions}>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton onClick={handleClick} selected={!open && isSelectedWithSubMenu}>
          <ListItemIcon>{sideBarItem.icon}</ListItemIcon>

          <ListItemText primary={sideBarItem.name} />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {sideBarItem.subMenu.map((subMenuItem) => {
              const route = PRIVATE_ROUTE_REGISTRY[subMenuItem.routeId];

              return (
                <PermissionHandler requiredPermissionCodes={route.anyOfPermissions} key={subMenuItem.routeId}>
                  <ListItem disablePadding sx={{ display: 'block' }} onClick={() => navigate(route.path)}>
                    <ListItemButton selected={pathname === route.path}>
                      <ListItemIcon>{subMenuItem.icon}</ListItemIcon>
                      <ListItemText primary={subMenuItem.name} />
                    </ListItemButton>
                  </ListItem>
                </PermissionHandler>
              );
            })}
          </List>
        </Collapse>
      </ListItem>
    </PermissionHandler>
  );
};

export default CollapseMenu;
